import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { AlertTriangle, CheckCircle, Plus, CreditCard as Edit2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMarketData } from '../contexts/MarketDataContext';
import { calculateSpreadCost, formatSpreadDisplay } from '../constants/spreadConfig';
import TakeProfitStopLossModal from './TakeProfitStopLossModal';

interface FuturesTradingFormsProps {
  usdtBalance: number;
  availableBalance?: number;
  maxAllowedLeverage?: number;
  minAllowedLeverage?: number;
  selectedPair: string;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onFuturesTrade: (
    symbol: string,
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit',
    price?: number
  ) => void;
}

const FuturesTradingForms: React.FC<FuturesTradingFormsProps> = ({
  usdtBalance,
  availableBalance,
  maxAllowedLeverage = 100,
  minAllowedLeverage = 1,
  selectedPair,
  calculateLiquidationPrice,
  onFuturesTrade
}) => {
  const { t } = useTranslation();
  const { getSnapshotPriceBySymbol, refreshSnapshot, lastSnapshotTime, isConnected: isRealtimeConnected, marketData } = useMarketData();
  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('isolated');
  const isLeverageLocked = minAllowedLeverage === maxAllowedLeverage;
  const [leverage, setLeverage] = useState(Math.min(Math.max(10, minAllowedLeverage), maxAllowedLeverage));
  const [orderType, setOrderType] = useState<'limit' | 'market'>('market');
  const [longAmount, setLongAmount] = useState('');
  const [shortAmount, setShortAmount] = useState('');
  const [longPercentage, setLongPercentage] = useState(0);
  const [shortPercentage, setShortPercentage] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Get current price from snapshot data (stable price)
  const livePairPrice = useMemo(() => {
    // Use snapshot price for stable trading experience
    const snapshotPrice = getSnapshotPriceBySymbol(selectedPair);
    if (snapshotPrice > 0) {
      return snapshotPrice;
    }

    // Fallback to market data context
    const marketDataItem = marketData.find(data => data.symbol === selectedPair);
    if (marketDataItem && marketDataItem.price > 0) {
      return marketDataItem.price;
    }

    return 0; // No fallback price
  }, [getSnapshotPriceBySymbol, selectedPair, marketData]);

  // Calculate amount from percentage for long positions
  const calculateLongAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    if (livePairPrice <= 0) return '';
    
    const targetMargin = usdtBalance * (percentage / 100);
    const amount = (targetMargin * leverage) / livePairPrice;
    
    return amount.toFixed(6);
  }, [usdtBalance, leverage, livePairPrice]);

  // Calculate amount from percentage for short positions
  const calculateShortAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    if (livePairPrice <= 0) return '';
    
    const targetMargin = usdtBalance * (percentage / 100);
    const amount = (targetMargin * leverage) / livePairPrice;
    
    return amount.toFixed(6);
  }, [usdtBalance, leverage, livePairPrice]);

  // Stop Loss / Take Profit states
  const [longStopLoss, setLongStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [longTakeProfit, setLongTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [shortStopLoss, setShortStopLoss] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);
  const [shortTakeProfit, setShortTakeProfit] = useState<{ trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number } | null>(null);

  // Modal states
  const [showLongSLModal, setShowLongSLModal] = useState(false);
  const [showLongTPModal, setShowLongTPModal] = useState(false);
  const [showShortSLModal, setShowShortSLModal] = useState(false);
  const [showShortTPModal, setShowShortTPModal] = useState(false);

  useEffect(() => {
    if (isLeverageLocked) {
      setLeverage(minAllowedLeverage);
    } else if (leverage < minAllowedLeverage) {
      setLeverage(minAllowedLeverage);
    } else if (leverage > maxAllowedLeverage) {
      setLeverage(maxAllowedLeverage);
    }
  }, [minAllowedLeverage, maxAllowedLeverage, isLeverageLocked]);

  const generateLeverageOptions = () => {
    const baseOptions = [1, 2, 3, 5, 10, 20, 25, 30, 50, 75, 100, 125, 150, 200, 250, 300, 400, 500, 750, 1000];
    return baseOptions.filter(option => option >= minAllowedLeverage && option <= maxAllowedLeverage);
  };

  const leverageOptions = generateLeverageOptions();
  const percentageOptions = [25, 50, 75, 100];

  // Clear error and success messages after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Real-time recalculation when leverage, price, or balance changes
  useEffect(() => {
    if (longPercentage > 0) {
      const newAmount = calculateLongAmountFromPercentage(longPercentage);
      setLongAmount(newAmount);
    }
  }, [leverage, longPercentage, calculateLongAmountFromPercentage]);

  useEffect(() => {
    if (shortPercentage > 0) {
      const newAmount = calculateShortAmountFromPercentage(shortPercentage);
      setShortAmount(newAmount);
    }
  }, [leverage, shortPercentage, calculateShortAmountFromPercentage]);

  // Calculate estimated PnL for stop loss and take profit
  const calculateEstimatedPnl = (
    side: 'long' | 'short',
    entryPrice: number,
    triggerPrice: number,
    amount: number,
    leverage: number
  ): number => {
    if (side === 'long') {
      return (triggerPrice - entryPrice) * amount * leverage;
    } else {
      return (entryPrice - triggerPrice) * amount * leverage;
    }
  };

  const handleLong = () => {
    const amount = parseFloat(longAmount);
    
    if (!amount || amount <= 0) {
      setErrorMessage('Please enter a valid amount');
      return;
    }

    const notionalValue = amount * livePairPrice;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    // Always pass the live price to ensure the parent component has it
    onFuturesTrade(selectedPair, 'long', amount, leverage, marginType, longStopLoss || undefined, longTakeProfit || undefined, orderType, livePairPrice);
    setSuccessMessage(`Long position opened successfully for ${amount} ${selectedPair.replace('USDT', '')}`);
    setLongAmount('');
    setLongPercentage(0);
    setLongStopLoss(null);
    setLongTakeProfit(null);
  };

  const handleShort = () => {
    const amount = parseFloat(shortAmount);
    
    if (!amount || amount <= 0) {
      setErrorMessage('Please enter a valid amount');
      return;
    }

    const notionalValue = amount * livePairPrice;
    const requiredMargin = notionalValue / leverage;
    
    if (requiredMargin > (availableBalance !== undefined ? availableBalance : usdtBalance)) {
      setErrorMessage('Insufficient available balance');
      return;
    }

    // Always pass the live price to ensure the parent component has it
    onFuturesTrade(selectedPair, 'short', amount, leverage, marginType, shortStopLoss || undefined, shortTakeProfit || undefined, orderType, livePairPrice);
    setSuccessMessage(`Short position opened successfully for ${amount} ${selectedPair.replace('USDT', '')}`);
    setShortAmount('');
    setShortPercentage(0);
    setShortStopLoss(null);
    setShortTakeProfit(null);
  };

  const handleLongPercentage = (percentage: number) => {
    setLongPercentage(percentage);
    const newAmount = calculateLongAmountFromPercentage(percentage);
    setLongAmount(newAmount);
  };

  const handleShortPercentage = (percentage: number) => {
    setShortPercentage(percentage);
    const newAmount = calculateShortAmountFromPercentage(percentage);
    setShortAmount(newAmount);
  };

  const calculateLongCost = () => {
    const amount = parseFloat(longAmount) || 0;
    const notionalValue = amount * livePairPrice;
    const requiredMargin = notionalValue / leverage;
    const spreadCost = amount > 0 ? calculateSpreadCost(selectedPair, livePairPrice, amount, 1) : 0;
    return requiredMargin + spreadCost;
  };

  const calculateShortCost = () => {
    const amount = parseFloat(shortAmount) || 0;
    const notionalValue = amount * livePairPrice;
    const requiredMargin = notionalValue / leverage;
    const spreadCost = amount > 0 ? calculateSpreadCost(selectedPair, livePairPrice, amount, 1) : 0;
    return requiredMargin + spreadCost;
  };


  const longSpreadInfo = longAmount ? formatSpreadDisplay(selectedPair, livePairPrice, parseFloat(longAmount), 1) : null;
  const shortSpreadInfo = shortAmount ? formatSpreadDisplay(selectedPair, livePairPrice, parseFloat(shortAmount), 1) : null;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      borderRadius: '20px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
    }}>
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 flex items-center gap-2 md:gap-3" style={{
          background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
          border: '1px solid rgba(220, 38, 38, 0.2)',
          borderRadius: '16px',
          padding: '16px'
        }}>
          <AlertTriangle size={20} style={{ color: '#dc2626' }} className="flex-shrink-0" />
          <span style={{ color: '#dc2626', fontWeight: 500 }}>{errorMessage}</span>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 md:mb-6 flex items-center gap-2 md:gap-3" style={{
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '16px',
          padding: '16px'
        }}>
          <CheckCircle size={20} style={{ color: '#16a34a' }} className="flex-shrink-0" />
          <span style={{ color: '#16a34a', fontWeight: 500 }}>{successMessage}</span>
        </div>
      )}

      {/* Margin Type and Leverage Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-8 gap-3 md:gap-0">
        <div className="flex items-center gap-3 md:gap-6">
          <div className="flex p-1" style={{
            background: 'rgba(0, 0, 0, 0.03)',
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.06)'
          }}>
            <button
              onClick={() => setMarginType('isolated')}
              className="px-4 py-2 text-sm font-medium transition-all duration-300"
              style={{
                borderRadius: '8px',
                background: marginType === 'isolated' ? 'linear-gradient(135deg, #d91222 0%, #f97316 100%)' : 'transparent',
                color: marginType === 'isolated' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                boxShadow: marginType === 'isolated' ? '0 4px 12px rgba(217, 18, 34, 0.25)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (marginType !== 'isolated') {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.color = '#000000';
                }
              }}
              onMouseLeave={(e) => {
                if (marginType !== 'isolated') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                }
              }}
            >
              {t('trading.isolated')}
            </button>
            <button
              onClick={() => setMarginType('cross')}
              className="px-4 py-2 text-sm font-medium transition-all duration-300"
              style={{
                borderRadius: '8px',
                background: marginType === 'cross' ? 'linear-gradient(135deg, #d91222 0%, #f97316 100%)' : 'transparent',
                color: marginType === 'cross' ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                boxShadow: marginType === 'cross' ? '0 4px 12px rgba(217, 18, 34, 0.25)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (marginType !== 'cross') {
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                  e.currentTarget.style.color = '#000000';
                }
              }}
              onMouseLeave={(e) => {
                if (marginType !== 'cross') {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                }
              }}
            >
              {t('trading.cross')}
            </button>
          </div>

          <div className="font-bold text-2xl" style={{
            background: 'linear-gradient(135deg, #d91222 0%, #f97316 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {leverage}x
          </div>
        </div>

        {/* Leverage Selector */}
        <div className="flex items-center gap-2 md:gap-4">
          <span className="text-xs md:text-sm" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>{t('common.leverage')}</span>
          <div className="flex gap-2">
            {leverageOptions.map((lev) => (
              <button
                key={lev}
                onClick={() => setLeverage(Math.min(lev, maxAllowedLeverage))}
                className="px-3 py-2 text-xs font-medium transition-all duration-300"
                style={{
                  borderRadius: '10px',
                  background: leverage === lev ? 'linear-gradient(135deg, #d91222 0%, #f97316 100%)' : 'rgba(0, 0, 0, 0.04)',
                  color: leverage === lev ? '#ffffff' : 'rgba(0, 0, 0, 0.6)',
                  border: leverage === lev ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: leverage === lev ? '0 4px 12px rgba(217, 18, 34, 0.25)' : 'none',
                  transform: leverage === lev ? 'scale(1.05)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  if (leverage !== lev) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.color = '#000000';
                  }
                }}
                onMouseLeave={(e) => {
                  if (leverage !== lev) {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  }
                }}
              >
                {lev}x
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex gap-3 md:gap-6 mb-4 md:mb-8">
        <button
          onClick={() => setOrderType('market')}
          className="text-sm font-medium px-4 py-2 transition-all duration-300"
          style={{
            borderRadius: '12px',
            background: orderType === 'market' ? 'rgba(217, 18, 34, 0.1)' : 'transparent',
            color: orderType === 'market' ? '#d91222' : 'rgba(0, 0, 0, 0.6)',
            border: orderType === 'market' ? '1px solid rgba(217, 18, 34, 0.3)' : '1px solid transparent',
            boxShadow: orderType === 'market' ? '0 4px 12px rgba(217, 18, 34, 0.15)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (orderType !== 'market') {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
              e.currentTarget.style.color = '#000000';
            }
          }}
          onMouseLeave={(e) => {
            if (orderType !== 'market') {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
            }
          }}
        >
          {t('trading.market')}
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className="text-sm font-medium px-4 py-2 transition-all duration-300"
          style={{
            borderRadius: '12px',
            background: orderType === 'limit' ? 'rgba(217, 18, 34, 0.1)' : 'transparent',
            color: orderType === 'limit' ? '#d91222' : 'rgba(0, 0, 0, 0.6)',
            border: orderType === 'limit' ? '1px solid rgba(217, 18, 34, 0.3)' : '1px solid transparent',
            boxShadow: orderType === 'limit' ? '0 4px 12px rgba(217, 18, 34, 0.15)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (orderType !== 'limit') {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
              e.currentTarget.style.color = '#000000';
            }
          }}
          onMouseLeave={(e) => {
            if (orderType !== 'limit') {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
            }
          }}
        >
          {t('trading.limit')}
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-8">
        {/* Long Form */}
        <div className="flex-1 relative overflow-hidden group" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
          border: '2px solid rgba(34, 197, 94, 0.2)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.08)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 197, 94, 0.16)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.08)';
        }}>
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.12) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-20px',
            left: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(22, 163, 74, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          {/* Arrow Up Symbol */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(34, 197, 94, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(34, 197, 94, 0.3)'
          }}>
            <div style={{
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '8px solid #16a34a'
            }} />
          </div>

          <div className="mb-6 relative z-10" style={{ paddingRight: '48px' }}>
            <div className="flex justify-between text-sm mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-2 font-mono" style={{ color: '#16a34a', fontWeight: 600 }}>
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USDT
                <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: '#16a34a' }}></div>
              </span>
            </div>
          </div>

          <div className="space-y-6 relative z-10">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm mb-1 md:mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>{t('common.price')} (USDT)</label>
              <input
                type="text"
                value={livePairPrice.toFixed(4)}
                className="w-full px-4 py-3 font-mono"
                style={{
                  background: 'rgba(34, 197, 94, 0.05)',
                  color: '#000000',
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                  borderRadius: '12px',
                  fontWeight: 600
                }}
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm mb-1 md:mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
                {t('trading.amount')} ({selectedPair.replace('USDT', '')})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Minimum 0.0001"
                  value={longAmount}
                  onChange={(e) => {
                    setLongAmount(e.target.value);
                    setLongPercentage(0);
                  }}
                  className="w-full px-4 py-3 pr-16 font-mono transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    color: '#000000',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: '12px',
                    fontWeight: 500
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.border = '2px solid rgba(34, 197, 94, 0.4)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(34, 197, 94, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.border = '1px solid rgba(34, 197, 94, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm font-medium" style={{ color: 'rgba(0, 0, 0, 0.5)' }} translate="no">
                  {selectedPair.replace('USDT', '')}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleLongPercentage(percentage)}
                  className="flex-1 py-2 text-xs font-medium transition-all duration-300"
                  style={{
                    borderRadius: '10px',
                    background: longPercentage === percentage ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : 'rgba(34, 197, 94, 0.1)',
                    color: longPercentage === percentage ? '#ffffff' : 'rgba(0, 0, 0, 0.7)',
                    border: longPercentage === percentage ? 'none' : '1px solid rgba(34, 197, 94, 0.2)',
                    boxShadow: longPercentage === percentage ? '0 4px 12px rgba(34, 197, 94, 0.3)' : 'none',
                    transform: longPercentage === percentage ? 'scale(1.05)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (longPercentage !== percentage) {
                      e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (longPercentage !== percentage) {
                      e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)';
                      e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)';
                    }
                  }}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss & Take Profit */}
            {longAmount && parseFloat(longAmount) > 0 && (
            <div className="pt-3 md:pt-6 mb-3" style={{ borderTop: '1px solid rgba(34, 197, 94, 0.1)' }}>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowLongSLModal(true)}
                  className="flex-1 py-3 text-sm font-medium transition-all"
                  style={{
                    borderRadius: '12px',
                    background: longStopLoss ? 'rgba(220, 38, 38, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                    border: longStopLoss ? '1px solid rgba(220, 38, 38, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
                    color: longStopLoss ? '#dc2626' : 'rgba(0, 0, 0, 0.6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = longStopLoss ? 'rgba(220, 38, 38, 0.15)' : 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = longStopLoss ? 'rgba(220, 38, 38, 0.1)' : 'rgba(0, 0, 0, 0.03)';
                  }}
                >
                  {longStopLoss ? <Edit2 size={14} className="inline mr-2" /> : <Plus size={14} className="inline mr-2" />}
                  {t('trading.stopLoss')}
                  {longStopLoss && (
                    <>
                      <span className="ml-2 font-mono">${longStopLoss.trigger_price.toFixed(2)}</span>
                      <X
                        size={14}
                        className="inline ml-2"
                        onClick={(e) => { e.stopPropagation(); setLongStopLoss(null); }}
                      />
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowLongTPModal(true)}
                  className="flex-1 py-3 text-sm font-medium transition-all"
                  style={{
                    borderRadius: '12px',
                    background: longTakeProfit ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0, 0, 0, 0.03)',
                    border: longTakeProfit ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
                    color: longTakeProfit ? '#16a34a' : 'rgba(0, 0, 0, 0.6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = longTakeProfit ? 'rgba(34, 197, 94, 0.2)' : 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = longTakeProfit ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0, 0, 0, 0.03)';
                  }}
                >
                  {longTakeProfit ? <Edit2 size={14} className="inline mr-2" /> : <Plus size={14} className="inline mr-2" />}
                  {t('trading.takeProfit')}
                  {longTakeProfit && (
                    <>
                      <span className="ml-2 font-mono">${longTakeProfit.trigger_price.toFixed(2)}</span>
                      <X
                        size={14}
                        className="inline ml-2"
                        onClick={(e) => { e.stopPropagation(); setLongTakeProfit(null); }}
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
            )}

            <div className="mb-3">
              <div className="space-y-2">
                <div className="flex justify-between text-sm" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                  <span>{t('trading.requiredMargin')}</span>
                  <span className="font-mono" style={{ color: '#000000', fontWeight: 600 }} translate="no">{calculateLongCost().toFixed(2)} USDT</span>
                </div>
                {longSpreadInfo && (
                  <div className="flex justify-between text-xs" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                    <span>Spread Cost ({longSpreadInfo.percentage})</span>
                    <span className="font-mono" style={{ color: '#f97316', fontWeight: 600 }} translate="no">{parseFloat(longSpreadInfo.cost).toFixed(4)} USDT</span>
                  </div>
                )}
                <div className="flex justify-between text-xs" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                  <span>{t('trading.liquidationPrice')}</span>
                  <span className="font-mono" style={{ color: '#dc2626', fontWeight: 600 }} translate="no">${calculateLiquidationPrice('long', livePairPrice, leverage, marginType).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleLong}
              className="w-full py-4 font-semibold transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
                color: '#ffffff',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 20px rgba(34, 197, 94, 0.3)',
                fontSize: '16px',
                letterSpacing: '0.02em',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(34, 197, 94, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(34, 197, 94, 0.3)';
              }}
            >
              {t('futures.buyLong')}
            </button>
          </div>
        </div>

        {/* Short Form */}
        <div className="flex-1 relative overflow-hidden group" style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
          border: '2px solid rgba(220, 38, 38, 0.2)',
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(220, 38, 38, 0.08)',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 24px rgba(220, 38, 38, 0.16)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.08)';
        }}>
          {/* Decorative Elements */}
          <div style={{
            position: 'absolute',
            top: '-30px',
            right: '-30px',
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(220, 38, 38, 0.12) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-20px',
            left: '-20px',
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(239, 68, 68, 0.08) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          {/* Arrow Down Symbol */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(220, 38, 38, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(220, 38, 38, 0.3)'
          }}>
            <div style={{
              width: '0',
              height: '0',
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid #dc2626'
            }} />
          </div>

          <div className="mb-6 relative z-10" style={{ paddingRight: '48px' }}>
            <div className="flex justify-between text-sm mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
              <span>{t('trading.availableMargin')}</span>
              <span className="flex items-center gap-2 font-mono" style={{ color: '#dc2626', fontWeight: 600 }}>
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USDT
                <div className="w-1 h-1 rounded-full animate-pulse" style={{ backgroundColor: '#dc2626' }}></div>
              </span>
            </div>
          </div>

          <div className="space-y-6 relative z-10">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm mb-1 md:mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>{t('common.price')} (USDT)</label>
              <input
                id="short-price-input"
                type="text"
                value={livePairPrice.toFixed(4)}
                className="w-full px-4 py-3 font-mono"
                style={{
                  background: 'rgba(220, 38, 38, 0.05)',
                  color: '#000000',
                  border: '1px solid rgba(220, 38, 38, 0.2)',
                  borderRadius: '12px',
                  fontWeight: 600
                }}
                readOnly
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm mb-1 md:mb-3" style={{ color: 'rgba(0, 0, 0, 0.6)', fontWeight: 500 }}>
                {t('trading.amount')} ({selectedPair.replace('USDT', '')})
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Minimum 0.0001"
                  value={shortAmount}
                  onChange={(e) => {
                    setShortAmount(e.target.value);
                    setShortPercentage(0);
                  }}
                  className="w-full px-4 py-3 pr-16 font-mono transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.8)',
                    color: '#000000',
                    border: '1px solid rgba(220, 38, 38, 0.2)',
                    borderRadius: '12px',
                    fontWeight: 500
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.border = '2px solid rgba(220, 38, 38, 0.4)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.border = '1px solid rgba(220, 38, 38, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sm font-medium" style={{ color: 'rgba(0, 0, 0, 0.5)' }} translate="no">
                  {selectedPair.replace('USDT', '')}
                </span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleShortPercentage(percentage)}
                  className="flex-1 py-2 text-xs font-medium transition-all duration-300"
                  style={{
                    borderRadius: '10px',
                    background: shortPercentage === percentage ? 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)' : 'rgba(220, 38, 38, 0.1)',
                    color: shortPercentage === percentage ? '#ffffff' : 'rgba(0, 0, 0, 0.7)',
                    border: shortPercentage === percentage ? 'none' : '1px solid rgba(220, 38, 38, 0.2)',
                    boxShadow: shortPercentage === percentage ? '0 4px 12px rgba(220, 38, 38, 0.3)' : 'none',
                    transform: shortPercentage === percentage ? 'scale(1.05)' : 'scale(1)'
                  }}
                  onMouseEnter={(e) => {
                    if (shortPercentage !== percentage) {
                      e.currentTarget.style.background = 'rgba(220, 38, 38, 0.2)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (shortPercentage !== percentage) {
                      e.currentTarget.style.background = 'rgba(220, 38, 38, 0.1)';
                      e.currentTarget.style.color = 'rgba(0, 0, 0, 0.7)';
                    }
                  }}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss & Take Profit */}
            {shortAmount && parseFloat(shortAmount) > 0 && (
            <div className="pt-3 md:pt-6 mb-3" style={{ borderTop: '1px solid rgba(220, 38, 38, 0.1)' }}>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowShortSLModal(true)}
                  className="flex-1 py-3 text-sm font-medium transition-all"
                  style={{
                    borderRadius: '12px',
                    background: shortStopLoss ? 'rgba(34, 197, 94, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                    border: shortStopLoss ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
                    color: shortStopLoss ? '#16a34a' : 'rgba(0, 0, 0, 0.6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = shortStopLoss ? 'rgba(34, 197, 94, 0.15)' : 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = shortStopLoss ? 'rgba(34, 197, 94, 0.1)' : 'rgba(0, 0, 0, 0.03)';
                  }}
                >
                  {shortStopLoss ? <Edit2 size={14} className="inline mr-2" /> : <Plus size={14} className="inline mr-2" />}
                  {t('trading.stopLoss')}
                  {shortStopLoss && (
                    <>
                      <span className="ml-2 font-mono">${shortStopLoss.trigger_price.toFixed(2)}</span>
                      <X
                        size={14}
                        className="inline ml-2"
                        onClick={(e) => { e.stopPropagation(); setShortStopLoss(null); }}
                      />
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowShortTPModal(true)}
                  className="flex-1 py-3 text-sm font-medium transition-all"
                  style={{
                    borderRadius: '12px',
                    background: shortTakeProfit ? 'rgba(220, 38, 38, 0.1)' : 'rgba(0, 0, 0, 0.03)',
                    border: shortTakeProfit ? '1px solid rgba(220, 38, 38, 0.3)' : '1px solid rgba(0, 0, 0, 0.08)',
                    color: shortTakeProfit ? '#dc2626' : 'rgba(0, 0, 0, 0.6)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = shortTakeProfit ? 'rgba(220, 38, 38, 0.15)' : 'rgba(0, 0, 0, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = shortTakeProfit ? 'rgba(220, 38, 38, 0.1)' : 'rgba(0, 0, 0, 0.03)';
                  }}
                >
                  {shortTakeProfit ? <Edit2 size={14} className="inline mr-2" /> : <Plus size={14} className="inline mr-2" />}
                  {t('trading.takeProfit')}
                  {shortTakeProfit && (
                    <>
                      <span className="ml-2 font-mono">${shortTakeProfit.trigger_price.toFixed(2)}</span>
                      <X
                        size={14}
                        className="inline ml-2"
                        onClick={(e) => { e.stopPropagation(); setShortTakeProfit(null); }}
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
            )}

            <div className="mb-3">
              <div className="space-y-2">
                <div className="flex justify-between text-sm" style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                  <span>{t('trading.requiredMargin')}</span>
                  <span className="font-mono" style={{ color: '#000000', fontWeight: 600 }} translate="no">{calculateShortCost().toFixed(2)} USDT</span>
                </div>
                {shortSpreadInfo && (
                  <div className="flex justify-between text-xs" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                    <span>Spread Cost ({shortSpreadInfo.percentage})</span>
                    <span className="font-mono" style={{ color: '#f97316', fontWeight: 600 }} translate="no">{parseFloat(shortSpreadInfo.cost).toFixed(4)} USDT</span>
                  </div>
                )}
                <div className="flex justify-between text-xs" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
                  <span>{t('trading.liquidationPrice')}</span>
                  <span className="font-mono" style={{ color: '#dc2626', fontWeight: 600 }} translate="no">${calculateLiquidationPrice('short', livePairPrice, leverage, marginType).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleShort}
              className="w-full py-4 font-semibold transition-all duration-300"
              style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                color: '#ffffff',
                borderRadius: '16px',
                border: 'none',
                boxShadow: '0 8px 20px rgba(220, 38, 38, 0.3)',
                fontSize: '16px',
                letterSpacing: '0.02em',
                textTransform: 'uppercase'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(220, 38, 38, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(220, 38, 38, 0.3)';
              }}
            >
              {t('futures.sellShort')}
            </button>
          </div>
        </div>
      </div>

      {/* Take Profit / Stop Loss Modals */}
      <TakeProfitStopLossModal
        isOpen={showLongSLModal}
        onClose={() => setShowLongSLModal(false)}
        type="stopLoss"
        side="long"
        entryPrice={livePairPrice}
        amount={parseFloat(longAmount) || 0}
        leverage={leverage}
        onConfirm={(price, type, execPrice) => {
          setLongStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showLongTPModal}
        onClose={() => setShowLongTPModal(false)}
        type="takeProfit"
        side="long"
        entryPrice={livePairPrice}
        amount={parseFloat(longAmount) || 0}
        leverage={leverage}
        onConfirm={(price, type, execPrice) => {
          setLongTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showShortSLModal}
        onClose={() => setShowShortSLModal(false)}
        type="stopLoss"
        side="short"
        entryPrice={livePairPrice}
        amount={parseFloat(shortAmount) || 0}
        leverage={leverage}
        onConfirm={(price, type, execPrice) => {
          setShortStopLoss({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />

      <TakeProfitStopLossModal
        isOpen={showShortTPModal}
        onClose={() => setShowShortTPModal(false)}
        type="takeProfit"
        side="short"
        entryPrice={livePairPrice}
        amount={parseFloat(shortAmount) || 0}
        leverage={leverage}
        onConfirm={(price, type, execPrice) => {
          setShortTakeProfit({ trigger_price: price, execution_type: type, execution_price: execPrice });
        }}
      />
    </div>
  );
};

export default FuturesTradingForms;